/**
 * ScheduleSpeakerView — facilitatorns körschema-presentationsläge.
 * Visar ett block i taget med delmoment, beskrivning och timers.
 * Navigerar med ← / → och uppdaterar currentBlock i Firestore → helskärm synkas.
 */
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, CircularProgress, Tooltip, IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import TvIcon from '@mui/icons-material/Tv';
import KeyboardIcon from '@mui/icons-material/Keyboard';
import TimerIcon from '@mui/icons-material/Timer';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { db } from '../../../../firebase/config';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import TimerOverlay from '../../../../components/TimerOverlay';
import DisplayModeBar from '../DisplayModeBar';

const accent = '#42A5F5'; // blå för körschema

export default function ScheduleSpeakerView({ sessionId }) {
  const navigate = useNavigate();
  const [schedule,     setSchedule]    = useState(null);
  const [title,        setTitle]       = useState('');
  const [wifiSsid,     setWifiSsid]    = useState('');
  const [wifiPassword, setWifiPassword]= useState('');
  const [loading,      setLoading]     = useState(true);
  const [timer,        setTimer]       = useState(null);
  const [displayMode,  setDisplayMode] = useState('blank');

  const blocks  = schedule?.blocks  || [];
  const current = schedule?.currentBlock ?? 0;
  const block   = blocks[current];

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'workshops', sessionId), snap => {
      if (snap.exists()) {
        const d = snap.data();
        setSchedule(d.schedule || null);
        setTitle(d.title || '');
        setWifiSsid(d.wifiSsid || '');
        setWifiPassword(d.wifiPassword || '');
        setDisplayMode(d.display?.mode || 'blank');
      }
      setLoading(false);
    });
    return unsub;
  }, [sessionId]);

  const goTo = useCallback(async (idx) => {
    if (!blocks.length || idx < 0 || idx >= blocks.length) return;
    await updateDoc(doc(db, 'workshops', sessionId), { 'schedule.currentBlock': idx });
  }, [sessionId, blocks.length]);

  useEffect(() => {
    const handle = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') goTo(current + 1);
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   goTo(current - 1);
    };
    window.addEventListener('keydown', handle);
    return () => window.removeEventListener('keydown', handle);
  }, [current, goTo]);

  if (loading) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: accent }} />
    </Box>
  );

  if (!schedule || blocks.length === 0) return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 2, p: 4 }}>
      <Typography color="text.secondary" textAlign="center">
        Inget körschema är skapat ännu.
      </Typography>
      <Button variant="outlined" onClick={() => navigate(`/workshopn/${sessionId}/schedule`)}
        sx={{ textTransform: 'none', borderColor: accent, color: accent }}>
        Skapa körschema
      </Button>
    </Box>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary', display: 'flex', flexDirection: 'column' }}>

      {/* Top bar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', px: 3, py: 1.25,
        borderBottom: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', gap: 2,
      }}>
        <Typography fontSize="0.65rem" fontWeight={700} color="text.disabled" letterSpacing={1.5}>
          KÖRSCHEMA
        </Typography>
        <Typography fontSize="0.72rem" color="text.secondary" sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {title}
        </Typography>

        <Tooltip title="Piltangenter ← → byter block">
          <KeyboardIcon sx={{ fontSize: 16, color: 'text.disabled' }} />
        </Tooltip>

        <Button size="small" variant="contained" startIcon={<TvIcon sx={{ fontSize: 15 }} />}
          onClick={async () => {
            await updateDoc(doc(db, 'workshops', sessionId), { 'display.mode': 'schedule' });
            const sw = window.screen.width, sh = window.screen.height;
            window.open(`/workshopn/${sessionId}/display`, 'workshop-display',
              `width=${sw},height=${sh},left=${sw},top=0,menubar=no,toolbar=no,location=no,status=no`);
          }}
          sx={{ textTransform: 'none', fontSize: '0.72rem', bgcolor: accent, '&:hover': { bgcolor: '#1976D2' }, px: 1.5 }}>
          Öppna HDMI-display
        </Button>

        <Button size="small" variant="outlined" startIcon={<OpenInNewIcon sx={{ fontSize: 15 }} />}
          onClick={() => navigate(`/workshopn/${sessionId}/schedule`)}
          sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: 'divider', color: 'text.secondary', px: 1.5 }}>
          Redigera
        </Button>

        {/* Progress dots */}
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 1 }}>
          {blocks.map((_, i) => (
            <Box key={i} onClick={() => goTo(i)}
              sx={{
                width: i === current ? 18 : 7, height: 7,
                borderRadius: 4,
                bgcolor: i === current ? accent : i < current ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)',
                cursor: 'pointer', transition: 'all 0.2s',
                '&:hover': { bgcolor: i === current ? accent : 'rgba(255,255,255,0.3)' },
              }} />
          ))}
          <Typography fontSize="0.65rem" color="text.disabled" ml={0.75}>
            {current + 1}/{blocks.length}
          </Typography>
        </Box>
      </Box>

      {/* Display mode bar */}
      <DisplayModeBar sessionId={sessionId} currentMode={displayMode} />

      {/* Main content */}
      <Box sx={{ flex: 1, maxWidth: 820, mx: 'auto', width: '100%', p: { xs: 3, md: 5 } }}>

        {/* Time + block number */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          {block.time && (
            <Typography sx={{ color: accent, fontFamily: 'monospace', fontWeight: 700, fontSize: '0.95rem' }}>
              {block.time}
            </Typography>
          )}
          <Typography sx={{ color: 'text.disabled', fontSize: '0.7rem', fontWeight: 700, letterSpacing: 2 }}>
            BLOCK {current + 1} AV {blocks.length}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {block.duration && (
            <Tooltip title={`Starta timer: ${block.duration} min`}>
              <Button
                size="small" variant="outlined"
                startIcon={<TimerIcon sx={{ fontSize: 15 }} />}
                onClick={() => setTimer({ label: block.title, totalSeconds: block.duration * 60 })}
                sx={{ textTransform: 'none', fontSize: '0.72rem', borderColor: accent, color: accent, px: 1.5 }}
              >
                {block.duration} min
              </Button>
            </Tooltip>
          )}
        </Box>

        {/* Title */}
        <Typography sx={{ fontSize: 'clamp(1.6rem, 3.5vw, 2.8rem)', fontWeight: 700, lineHeight: 1.15, mb: 1.5 }}>
          {block.title}
        </Typography>

        {/* Description */}
        {block.description && (
          <Typography sx={{ color: 'text.secondary', fontSize: '1rem', mb: 3, lineHeight: 1.6 }}>
            {block.description}
          </Typography>
        )}

        {/* Sub-items */}
        {block.subItems?.length > 0 && (
          <Box sx={{
            mt: block.description ? 0 : 2,
            p: 2.5, borderRadius: 2,
            bgcolor: 'rgba(255,255,255,0.03)',
            border: '1px solid', borderColor: 'divider',
          }}>
            <Typography fontSize="0.65rem" color="text.disabled" fontWeight={700} letterSpacing={1} mb={1.5}>
              DELMOMENT
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {block.subItems.map((sub, si) => (
                <Box key={si} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    minWidth: 52, px: 1, py: 0.3, borderRadius: 1,
                    bgcolor: accent + '18', border: `1px solid ${accent}33`,
                    textAlign: 'center', flexShrink: 0,
                  }}>
                    <Typography sx={{ color: accent, fontSize: '0.75rem', fontWeight: 700, fontFamily: 'monospace' }}>
                      {sub.duration} min
                    </Typography>
                  </Box>
                  <Typography sx={{ color: 'text.primary', fontSize: '0.95rem', flex: 1 }}>
                    {sub.text}
                  </Typography>
                  <Tooltip title={`Timer: ${sub.duration} min`}>
                    <IconButton size="small"
                      onClick={() => setTimer({ label: sub.text, totalSeconds: sub.duration * 60 })}
                      sx={{ color: 'text.disabled', '&:hover': { color: accent } }}>
                      <TimerIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              ))}
            </Box>
          </Box>
        )}

        {/* WiFi-info */}
        {wifiSsid && (
          <Box sx={{ mt: 3, p: 2, borderRadius: 1.5, bgcolor: 'rgba(255,255,255,0.025)', border: '1px solid rgba(255,255,255,0.07)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography fontSize="0.62rem" color="text.disabled" letterSpacing={1} fontWeight={600}>
              WIFI
            </Typography>
            <Typography fontSize="0.88rem" fontWeight={700} color="text.primary">{wifiSsid}</Typography>
            {wifiPassword && (
              <>
                <Typography fontSize="0.7rem" color="text.disabled">·</Typography>
                <Typography fontSize="0.88rem" color="text.secondary" fontFamily="monospace">{wifiPassword}</Typography>
              </>
            )}
          </Box>
        )}

        {/* Block thumbnails */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 4 }}>
          {blocks.map((b, i) => (
            <Box key={i} onClick={() => goTo(i)}
              sx={{
                px: 1.25, py: 0.35, borderRadius: 1,
                fontSize: '0.62rem', fontWeight: 600,
                bgcolor: i === current ? accent : i < current ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.05)',
                color: i === current ? '#fff' : i < current ? 'text.disabled' : 'text.disabled',
                cursor: 'pointer',
                border: '1px solid',
                borderColor: i === current ? accent : 'transparent',
                opacity: i < current ? 0.5 : 1,
                transition: 'all 0.15s',
                '&:hover': { bgcolor: i === current ? accent : 'rgba(255,255,255,0.1)', opacity: 1 },
                whiteSpace: 'nowrap',
              }}
            >
              {i + 1}. {b.title || `Block ${i + 1}`}
            </Box>
          ))}
        </Box>
      </Box>

      {/* Navigation footer */}
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        px: 3, py: 2,
        borderTop: '1px solid', borderColor: 'divider',
        bgcolor: 'background.paper', gap: 2,
      }}>
        <Button
          variant="outlined" startIcon={<ArrowBackIcon />}
          disabled={current === 0}
          onClick={() => goTo(current - 1)}
          sx={{ textTransform: 'none', borderColor: 'divider', color: 'text.secondary', '&:hover': { borderColor: accent, color: accent }, minWidth: 150 }}
        >
          Föregående block
        </Button>

        {/* Current block summary */}
        <Box sx={{ textAlign: 'center' }}>
          <Typography fontSize="0.7rem" color="text.disabled">
            {block.time || `Block ${current + 1}`}
            {block.duration ? ` · ${block.duration} min` : ''}
          </Typography>
        </Box>

        <Button
          variant="contained"
          endIcon={<ArrowForwardIcon />}
          disabled={current === blocks.length - 1}
          onClick={() => goTo(current + 1)}
          sx={{ bgcolor: accent, '&:hover': { bgcolor: '#1976D2' }, textTransform: 'none', fontWeight: 600, minWidth: 150 }}
        >
          Nästa block
        </Button>
      </Box>

      {/* Timer overlay */}
      {timer && (
        <TimerOverlay
          open
          label={timer.label}
          totalSeconds={timer.totalSeconds}
          color={accent}
          onClose={() => setTimer(null)}
        />
      )}
    </Box>
  );
}
